import { Fragment } from "react";
import { Box, Button, ButtonBase, MenuItem, TextField } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { brainTokens } from "../../../design/brainTheme";
import { Icon } from "../../../mui";
import { JiraScopesCard } from "../JiraScopesCard";
import type { Project, ProjectKind, ProjectStatus } from "../../../lib/brain-api";

const c = brainTokens.color;

const splitList = (s: string): string[] =>
  [...new Set(s.split(",").map((x) => x.trim().toLowerCase()).filter(Boolean))];

function jiraEpicValue(project: Project | null, jiraKey?: string): string {
  if (jiraKey) return jiraKey;
  if (project?.jiraScope?.type === "epic") return project.jiraScope.key;
  const epic = project?.jiraScopes?.find((s) => s.scope.type === "epic");
  if (epic && epic.scope.type === "epic") return epic.scope.key;
  return "—";
}

function ListField({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string[];
  onChange: (v: string[]) => void;
  placeholder?: string;
}) {
  return (
    <TextField
      label={label}
      size="small"
      fullWidth
      defaultValue={value.join(", ")}
      placeholder={placeholder}
      onBlur={(e) => onChange(splitList(e.target.value))}
      InputProps={{ sx: fieldSx }}
      InputLabelProps={{ sx: { fontSize: 12.5 } }}
    />
  );
}

const fieldSx = {
  fontSize: 12.5,
  "& .MuiOutlinedInput-input": { py: "6px", px: "9px", fontSize: 12.5 },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: c.border, borderRadius: "6px" },
} as const;

function statusWord(s: string | undefined): string {
  const k = (s ?? "").toLowerCase();
  if (k === "blocked") return "Blocked";
  if (k === "done" || k === "shipped") return "Delivered";
  if (k === "draft") return "Draft";
  if (k === "planning" || k === "planned") return "Planning";
  if (k === "on_hold") return "On hold";
  if (k === "with_client") return "With client";
  if (k === "archived") return "Archived";
  if (k === "in_progress") return "In progress";
  return s ? s.replace(/_/g, " ") : "—";
}

function joinDot(parts: string[]): string {
  return parts.filter(Boolean).join(" · ") || "—";
}

type RecordRow = {
  label: string;
  value: string;
  mono?: boolean;
  linkTo?: string;
  chips?: string[];
  chipTone?: "neutral" | "teal";
  tone?: "status";
};

/** Mono micro-chip — neutral (Systems/Repos) or teal (Tags), per the record-rail spec. */
function RecordChip({ children, tone }: { children: string; tone: "neutral" | "teal" }) {
  const neutral = tone === "neutral";
  return (
    <Box
      component="span"
      sx={{
        fontFamily: brainTokens.font.mono,
        fontSize: 11,
        borderRadius: "5px",
        padding: "1px 6px",
        background: neutral ? c.panelBg2 : c.tealTint,
        border: `1px solid ${neutral ? c.borderLight : c.tealTintBorder}`,
        color: neutral ? c.ink3 : c.tealInk,
      }}
    >
      {children}
    </Box>
  );
}

export function RecordRail({
  project,
  draft,
  editing,
  canEdit,
  busy,
  customerId,
  customerName,
  owner,
  jiraKey,
  fileCount,
  atomCount,
  lastActivityLine,
  standupMentionsThisWeek,
  onEdit,
  onCancel,
  onSave,
  onDelete,
  setDraft,
  onScopesChanged,
}: {
  project: Project | null;
  draft: Project | null;
  editing: boolean;
  canEdit: boolean;
  busy: boolean;
  customerId?: string;
  customerName?: string;
  owner?: string;
  jiraKey?: string;
  fileCount: number;
  atomCount: number;
  lastActivityLine: string;
  standupMentionsThisWeek: number;
  onEdit: () => void;
  onCancel: () => void;
  onSave: () => void;
  onDelete: () => void;
  setDraft: (patch: Partial<Project>) => void;
  onScopesChanged: () => void;
}) {
  const set = (patch: Partial<Project>) => setDraft(patch);
  const setLinks = (patch: Partial<Project["links"]>) => {
    if (!draft) return;
    setDraft({ links: { ...draft.links, ...patch } });
  };

  const rows: RecordRow[] = [
    { label: "Status", value: statusWord(project?.blocked ? "blocked" : project?.status), tone: "status" },
    { label: "Kind", value: project?.kind ?? "—" },
    {
      label: "Customer",
      value: customerName ?? customerId ?? "—",
      linkTo: customerId ? `/customers/${encodeURIComponent(customerId)}` : undefined,
    },
    { label: "Owner", value: owner ?? "—", mono: true },
    { label: "Lead", value: project?.assignee ?? "—", mono: true },
    { label: "Deputy", value: project?.deputy ?? "—", mono: true },
    { label: "Reporter", value: project?.reporter ?? "—", mono: true },
    { label: "Priority", value: project?.priority ?? "—", mono: true },
    { label: "Systems", value: joinDot(project?.systems ?? []), chips: project?.systems ?? [], chipTone: "neutral" },
    { label: "Repos", value: joinDot(project?.links?.repos ?? []), chips: project?.links?.repos ?? [], chipTone: "neutral" },
    { label: "Tags", value: joinDot(project?.tags ?? []), chips: project?.tags ?? [], chipTone: "teal" },
    { label: "Jira epic", value: jiraEpicValue(project, jiraKey), mono: true },
  ];

  return (
    <Box
      sx={{
        width: "100%",
        background: c.white,
        border: `1px solid ${c.border}`,
        borderRadius: `${brainTokens.radius.card}px`,
        padding: "18px 20px",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <Box
          sx={{
            fontFamily: brainTokens.font.mono,
            fontSize: 10,
            letterSpacing: "0.12em",
            color: c.faint,
          }}
        >
          RECORD
        </Box>
        <Box sx={{ flex: 1 }} />
        {canEdit && (
          <Box
            component={ButtonBase}
            onClick={() => (editing ? undefined : onEdit())}
            sx={{
              fontSize: 12.5,
              color: c.tealInk,
              fontFamily: "inherit",
              lineHeight: 1.2,
              cursor: editing ? "default" : "pointer",
            }}
          >
            {editing ? "Editing" : "Edit"}
          </Box>
        )}
      </Box>

      {!editing || !draft ? (
        <Box
          sx={{
            mt: "12px",
            display: "grid",
            gridTemplateColumns: "88px 1fr",
            rowGap: "11px",
            columnGap: "12px",
            fontSize: 13,
          }}
        >
          {rows.map((r) => {
            const empty = !r.value || r.value === "—";
            return (
              <Fragment key={r.label}>
                <Box component="span" sx={{ color: c.muted, alignSelf: "start" }}>
                  {r.label}
                </Box>
                {r.chips?.length ? (
                  <Box key={`${r.label}-v`} sx={{ display: "flex", flexWrap: "wrap", gap: "5px" }}>
                    {r.chips.map((v) => (
                      <RecordChip key={v} tone={r.chipTone ?? "neutral"}>
                        {v}
                      </RecordChip>
                    ))}
                  </Box>
                ) : r.linkTo && !empty ? (
                  <Box
                    key={`${r.label}-v`}
                    component={RouterLink}
                    to={r.linkTo}
                    sx={{
                      fontSize: 13,
                      color: c.tealInk,
                      textDecoration: "none",
                      minWidth: 0,
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    {r.value}
                  </Box>
                ) : (
                  <Box
                    key={`${r.label}-v`}
                    component="span"
                    sx={{
                      fontSize: r.tone === "status" ? 12 : 13,
                      fontWeight: r.tone === "status" ? 600 : 400,
                      color: empty ? c.placeholder : r.tone === "status" ? c.tealInk : c.ink3,
                      fontFamily: r.mono ? brainTokens.font.mono : "inherit",
                      minWidth: 0,
                    }}
                  >
                    {empty ? "—" : r.value}
                  </Box>
                )}
              </Fragment>
            );
          })}
        </Box>
      ) : (
        <Box
          sx={{
            mt: "8px",
            border: `1px solid ${c.border}`,
            borderRadius: `${brainTokens.radius.card}px`,
            p: "14px",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: "9px" }}>
            <TextField
              label="Name"
              size="small"
              fullWidth
              value={draft.name}
              onChange={(e) => set({ name: e.target.value })}
              InputProps={{ sx: fieldSx }}
            />
            <TextField
              select
              label="Status"
              size="small"
              fullWidth
              value={draft.status}
              onChange={(e) => set({ status: e.target.value as ProjectStatus })}
              InputProps={{ sx: fieldSx }}
            >
              <MenuItem value="planning">planning</MenuItem>
              <MenuItem value="in_progress">in progress</MenuItem>
              <MenuItem value="on_hold">on hold</MenuItem>
              <MenuItem value="with_client">with client</MenuItem>
              <MenuItem value="done">delivered</MenuItem>
              <MenuItem value="archived">archived</MenuItem>
            </TextField>
            <TextField
              select
              label="Kind"
              size="small"
              fullWidth
              value={draft.kind}
              onChange={(e) => set({ kind: e.target.value as ProjectKind })}
              InputProps={{ sx: fieldSx }}
            >
              <MenuItem value="internal">internal</MenuItem>
              <MenuItem value="customer">customer</MenuItem>
            </TextField>
            <TextField
              label="Customer (id)"
              size="small"
              fullWidth
              defaultValue={draft.customer ?? ""}
              onBlur={(e) => set({ customer: e.target.value.trim().toLowerCase() || undefined })}
              placeholder="flightcare"
              InputProps={{ sx: fieldSx }}
            />
            <TextField
              label="Owner"
              size="small"
              fullWidth
              defaultValue={draft.owner ?? ""}
              onBlur={(e) => set({ owner: e.target.value.trim().toLowerCase() || undefined })}
              placeholder="isaac"
              InputProps={{ sx: fieldSx }}
            />
            <TextField
              label="Lead"
              size="small"
              fullWidth
              value={draft.assignee ?? ""}
              onChange={(e) => set({ assignee: e.target.value || undefined })}
              InputProps={{ sx: fieldSx }}
            />
            <TextField
              label="Deputy"
              size="small"
              fullWidth
              value={draft.deputy ?? ""}
              onChange={(e) => set({ deputy: e.target.value || undefined })}
              InputProps={{ sx: fieldSx }}
            />
            <TextField
              label="Reporter"
              size="small"
              fullWidth
              value={draft.reporter ?? ""}
              onChange={(e) => set({ reporter: e.target.value || undefined })}
              InputProps={{ sx: fieldSx }}
            />
            <TextField
              select
              label="Priority"
              size="small"
              fullWidth
              value={draft.priority ?? ""}
              onChange={(e) => set({ priority: e.target.value || undefined })}
              InputProps={{ sx: fieldSx }}
            >
              <MenuItem value="">—</MenuItem>
              <MenuItem value="P1">P1</MenuItem>
              <MenuItem value="P2">P2</MenuItem>
              <MenuItem value="P3">P3</MenuItem>
            </TextField>
            <TextField
              label="Jira epic"
              size="small"
              fullWidth
              defaultValue={draft.links.jira_epic ?? ""}
              onBlur={(e) => setLinks({ jira_epic: e.target.value.trim().toLowerCase() || undefined })}
              placeholder="fc-100"
              InputProps={{ sx: fieldSx }}
            />
            <ListField label="Systems" value={draft.systems} onChange={(v) => set({ systems: v })} placeholder="aeronet-roxen" />
            <ListField
              label="Repos"
              value={draft.links.repos}
              onChange={(v) => setLinks({ repos: v })}
              placeholder="aeronet-frontend"
            />
            <ListField label="Tags" value={draft.tags} onChange={(v) => set({ tags: v })} placeholder="onboarding" />
          </Box>

          <Box sx={{ display: "flex", gap: "8px", mt: "16px", flexWrap: "wrap" }}>
            <Button size="small" variant="contained" disabled={busy} onClick={onSave}>
              Save record
            </Button>
            <Button size="small" onClick={onCancel} sx={{ color: c.ink3 }}>
              Cancel
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={busy}
              onClick={onDelete}
              startIcon={<Icon name="trash" size={14} stroke={1.8} />}
              sx={{
                ml: "auto",
                color: c.dangerText2,
                borderColor: c.dangerBorder,
                "&:hover": { background: c.dangerBg, borderColor: c.dangerBorder },
              }}
            >
              Delete
            </Button>
          </Box>
        </Box>
      )}

      <Box sx={{ mt: "16px", pt: "14px", borderTop: `1px solid ${c.borderLight}` }}>
        <Box
          sx={{
            fontFamily: brainTokens.font.mono,
            fontSize: 10,
            letterSpacing: "0.12em",
            color: c.faint,
            mb: "8px",
          }}
        >
          JIRA SCOPES · WORKSTREAMS
        </Box>
        <JiraScopesCard
          projectId={project?.projectId ?? ""}
          scopes={project?.jiraScopes}
          legacyScope={project?.jiraScope ?? null}
          canEdit={canEdit}
          onChanged={onScopesChanged}
          embedded
        />
      </Box>
    </Box>
  );
}
